import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, Crown, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "../api";
import { useAuth } from "../context/AuthContext";

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => {
    api.get("/membership/plans").then(({ data }) => setPlans(data.plans)).catch(() => {});
  }, []);

  const subscribe = async (plan) => {
    if (!user) {
      navigate("/register");
      return;
    }
    setBusyKey(plan.key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: plan.lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setBusyKey(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
      <div className="text-center mb-14">
        <p className="eyebrow mb-3">Membership</p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-stone-900">Invest in something real</h1>
        <p className="text-stone-600 mt-4 max-w-xl mx-auto">
          Every plan includes access to our 100% video-verified community. Secure checkout by Stripe — cancel anytime.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan, i) => {
          const current = user?.membership === plan.key;
          const featured = plan.key === "gold";
          return (
            <div
              key={plan.key}
              className={`card-surface p-8 flex flex-col relative ${featured ? "ring-2 ring-[#C58342] shadow-md" : ""}`}
              data-testid={`pricing-plan-${plan.key}`}
            >
              {featured && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-semibold tracking-wide uppercase px-3 py-1 rounded-full bg-[#C58342] text-white inline-flex items-center gap-1">
                  <Crown size={11} /> Most popular
                </span>
              )}
              <h3 className="font-display text-2xl text-stone-900">{plan.name}</h3>
              <p className="text-sm text-stone-500 mt-1">{plan.tagline}</p>
              <p className="mt-5">
                <span className="font-display text-4xl text-[#1F3A34]">${plan.price.toFixed(0)}</span>
                <span className="text-sm text-stone-500"> / month</span>
              </p>
              <ul className="mt-6 space-y-2.5 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-stone-600">
                    <BadgeCheck size={15} className="mt-0.5 shrink-0 text-[#065F46]" />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => subscribe(plan)}
                disabled={current || busyKey === plan.key}
                className={`${featured ? "btn-accent" : "btn-primary"} w-full mt-8 disabled:opacity-60`}
                data-testid={`pricing-subscribe-plan-btn-${plan.key}`}
              >
                {current ? "Your current plan" : busyKey === plan.key ? "Redirecting…" : "Subscribe"}
              </button>
            </div>
          );
        })}
      </div>

      <p className="text-center text-xs text-stone-400 mt-10 flex items-center justify-center gap-1.5">
        <ShieldCheck size={13} /> Payments processed securely by Stripe · SSL encrypted · Test card: 4242 4242 4242 4242
      </p>
    </div>
  );
}
